import React, { useMemo } from "react";
import { Badge, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import CustomToggleButton from "../customToggleButton/customToggleButton";
import {
  AUTO_HIKE_SCOPE,
  HIKE_TYPE,
  buildAutoHikePreviewLadder,
  priceAfterManualThenAuto,
  resolveAutoStatusForArea,
  sectionMatchesAutoScope,
} from "../../helpers/tableBookingAutoPriceHike";
import {
  flattenLayoutPricingRows,
  indexAreaPricingRows,
  resolveEffectiveEventPricing,
} from "../../helpers/eventTableBooking";
import {
  liveStatusKeyFromSheetTable,
  resolveLiveSheetTable,
} from "../../helpers/eventTableBookingLiveStatus";
import { formatInr } from "../../helpers/ticketSalePhases";
import "../../pages/eventPricingHub/eventPricingHub.css";

const TableAutoPriceHikePanel = ({
  autoHike,
  onChange,
  manualHike,
  layout,
  layoutLoading,
  liveIndex,
  areaPricing,
  tableBookingEnabled,
  autoStatus,
}) => {
  const { t } = useTranslation();
  const rows = useMemo(() => flattenLayoutPricingRows(layout), [layout]);
  const tableRows = useMemo(
    () => rows.filter((r) => r.kind === "table"),
    [rows]
  );
  const zoneRows = useMemo(() => rows.filter((r) => r.kind === "zone"), [rows]);
  const overrideIndex = useMemo(
    () => indexAreaPricingRows(areaPricing),
    [areaPricing]
  );

  const setAuto = (patch) => onChange({ ...autoHike, ...patch });

  const sampleBase = useMemo(() => {
    const first = tableRows.find((row) =>
      sectionMatchesAutoScope(row.areaId, { ...autoHike, enabled: true })
    ) || tableRows[0];
    if (!first) return 0;
    return Number(resolveEffectiveEventPricing(first, overrideIndex)?.price) || 0;
  }, [tableRows, overrideIndex, autoHike]);

  const ladder = useMemo(() => {
    const afterManual = priceAfterManualThenAuto(
      sampleBase,
      manualHike,
      { enabled: false },
      0
    );
    return buildAutoHikePreviewLadder(afterManual, autoHike, 4);
  }, [autoHike, manualHike, sampleBase]);

  const globalStatus =
    autoStatus && !Array.isArray(autoStatus.sections) ? autoStatus : null;

  const floorPreview = useMemo(() => {
    return tableRows.map((row) => {
      const live = resolveLiveSheetTable(row, liveIndex);
      const liveKey = liveStatusKeyFromSheetTable(live);
      const booked = liveKey && liveKey !== "available";
      const layoutPrice =
        Number(resolveEffectiveEventPricing(row, overrideIndex)?.price) || 0;
      const inScope = sectionMatchesAutoScope(row.areaId, autoHike);
      const areaStatus = resolveAutoStatusForArea(autoStatus, row.areaId);
      const tierIndex = inScope
        ? Number(areaStatus?.currentTierIndex ?? globalStatus?.currentTierIndex ?? 0)
        : 0;
      const effective = inScope
        ? priceAfterManualThenAuto(layoutPrice, manualHike, autoHike, tierIndex)
        : priceAfterManualThenAuto(layoutPrice, manualHike, { enabled: false }, 0);
      return { row, booked, layoutPrice, effective, inScope, tierIndex };
    });
  }, [
    tableRows,
    liveIndex,
    overrideIndex,
    autoHike,
    manualHike,
    autoStatus,
    globalStatus,
  ]);

  if (!tableBookingEnabled) {
    return (
      <div className="eph-empty">
        <i className="bx bx-grid-alt" />
        <strong>{t("EVENT_HIKE_NEED_TABLES_TITLE")}</strong>
        <span>{t("EVENT_HIKE_NEED_TABLES")}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="eph-tier-head">
        <div>
          <div className="fw-semibold">{t("EVENT_AUTO_HIKE_TOGGLE")}</div>
          <div className="eph-tier-meta">{t("EVENT_AUTO_HIKE_TOGGLE_HINT")}</div>
        </div>
        <CustomToggleButton
          id="table-auto-price-hike"
          checked={Boolean(autoHike?.enabled)}
          onCheckChange={(checked) => setAuto({ enabled: checked })}
        />
      </div>

      {!autoHike?.enabled ? (
        <p className="eph-tier-meta mt-3 mb-0">{t("EVENT_AUTO_HIKE_OFF_HINT")}</p>
      ) : (
        <>
          <p className="eph-info-note">{t("EVENT_AUTO_HIKE_EVENT_ONLY")}</p>

          <div className="hike-scope" role="group" aria-label={t("EVENT_HIKE_SCOPE")}>
            {[
              { id: AUTO_HIKE_SCOPE.ALL, label: t("EVENT_HIKE_SCOPE_ALL") },
              {
                id: AUTO_HIKE_SCOPE.SECTION,
                label: t("EVENT_AUTO_HIKE_SCOPE_SECTION"),
              },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={autoHike.scope === opt.id ? "is-on" : ""}
                onClick={() => setAuto({ scope: opt.id })}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {autoHike.scope === AUTO_HIKE_SCOPE.SECTION ? (
            <div className="eph-zone-pills">
              {zoneRows.map((zone) => {
                const checked = (autoHike.areaIds || []).includes(zone.areaId);
                return (
                  <button
                    key={zone.areaId}
                    type="button"
                    className={`eph-chip${checked ? " is-on" : ""}`}
                    onClick={() => {
                      const next = new Set(autoHike.areaIds || []);
                      if (checked) next.delete(zone.areaId);
                      else next.add(zone.areaId);
                      setAuto({ areaIds: [...next] });
                    }}
                  >
                    {zone.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="eph-hike-fields eph-auto-fields">
            <div>
              <Form.Label>{t("EVENT_AUTO_HIKE_N_LABEL")}</Form.Label>
              <Form.Control
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={autoHike.bookingsPerTier ?? ""}
                onChange={(e) => setAuto({ bookingsPerTier: e.target.value })}
              />
            </div>
            <div>
              <Form.Label>{t("EVENT_HIKE_TYPE")}</Form.Label>
              <Form.Select
                value={autoHike.hikeType}
                onChange={(e) => setAuto({ hikeType: e.target.value })}
              >
                <option value={HIKE_TYPE.PERCENT}>
                  {t("EVENT_HIKE_TYPE_PERCENT")}
                </option>
                <option value={HIKE_TYPE.FIXED}>
                  {t("EVENT_HIKE_TYPE_FIXED")}
                </option>
              </Form.Select>
            </div>
            <div>
              <Form.Label>{t("EVENT_HIKE_VALUE")}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                inputMode="decimal"
                value={autoHike.hikeValue ?? ""}
                onChange={(e) => setAuto({ hikeValue: e.target.value })}
              />
            </div>
            <p className="eph-tier-meta eph-field-hint mb-0">
              {t("EVENT_AUTO_HIKE_N_HINT")}
            </p>
          </div>

          <div className="eph-auto-split">
            <div>
              <div className="eph-subsection-title fw-semibold mb-2">
                {t("EVENT_AUTO_HIKE_LADDER")}
              </div>
              <div className="eph-ladder">
                {ladder.map((step) => (
                  <div key={step.tierIndex} className="eph-ladder-row">
                    <span>
                      {t("EVENT_AUTO_HIKE_TIER_RANGE", {
                        tier: step.tierIndex,
                        from: step.fromCount,
                        to: step.toCount,
                      })}
                    </span>
                    <strong>{formatInr(step.price)}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="eph-subsection-title fw-semibold mb-2">
                {t("EVENT_AUTO_HIKE_LIVE")}
              </div>
              {globalStatus ? (
                <div className="eph-stats">
                  <div className="eph-stat">
                    <span>{t("EVENT_AUTO_HIKE_SOLD")}</span>
                    <strong>{globalStatus.soldCount ?? 0}</strong>
                  </div>
                  <div className="eph-stat">
                    <span>{t("EVENT_AUTO_HIKE_TIER")}</span>
                    <strong>{globalStatus.currentTierIndex ?? 0}</strong>
                  </div>
                  <div className="eph-stat">
                    <span>{t("EVENT_AUTO_HIKE_UNTIL")}</span>
                    <strong>{globalStatus.bookingsUntilNextTier ?? "—"}</strong>
                  </div>
                </div>
              ) : Array.isArray(autoStatus?.sections) &&
                autoStatus.sections.length ? (
                autoStatus.sections.map((sec) => {
                  const zone = zoneRows.find(
                    (z) => String(z.areaId) === String(sec.areaId)
                  );
                  return (
                    <div key={sec.areaId} className="eph-tier-meta mb-2">
                      <strong>{zone?.label || sec.areaId}</strong>
                      {" · "}
                      {t("EVENT_AUTO_HIKE_SECTION_LIVE", {
                        sold: sec.soldCount ?? 0,
                        tier: sec.currentTierIndex ?? 0,
                        n: sec.bookingsUntilNextTier ?? "—",
                      })}
                    </div>
                  );
                })
              ) : (
                <p className="eph-live-empty">{t("EVENT_AUTO_HIKE_NO_LIVE")}</p>
              )}
            </div>
          </div>

          {layoutLoading ? (
            <Spinner animation="border" size="sm" />
          ) : !layout ? (
            <p className="eph-tier-meta">{t("EVENT_HIKE_NO_LAYOUT")}</p>
          ) : (
            <div className="table-responsive mt-3">
              <div className="eph-subsection-title fw-semibold mb-2">
                {t("EVENT_AUTO_HIKE_FLOOR")}
              </div>
              <Table className="hike-preview-table" size="sm" hover>
                <thead>
                  <tr>
                    <th>{t("EVENT_HIKE_COL_SECTION")}</th>
                    <th>{t("EVENT_HIKE_COL_TABLE")}</th>
                    <th>{t("EVENT_HIKE_COL_STATUS")}</th>
                    <th>{t("EVENT_HIKE_COL_PRICE")}</th>
                  </tr>
                </thead>
                <tbody>
                  {floorPreview.map(({ row, booked, layoutPrice, effective, inScope }) => (
                    <tr key={`${row.areaId}:${row.tableId}`}>
                      <td data-label={t("EVENT_HIKE_COL_SECTION")}>
                        {row.zoneLabel || "—"}
                      </td>
                      <td data-label={t("EVENT_HIKE_COL_TABLE")}>{row.label}</td>
                      <td data-label={t("EVENT_HIKE_COL_STATUS")}>
                        <Badge className={booked ? "badge-sold" : "badge-open"}>
                          {booked ? t("EVENT_HIKE_SOLD") : t("EVENT_HIKE_AVAILABLE")}
                        </Badge>
                      </td>
                      <td data-label={t("EVENT_HIKE_COL_PRICE")}>
                        {inScope && effective !== layoutPrice ? (
                          <>
                            <span className="price-strike">{formatInr(layoutPrice)}</span>
                            <span className="price-new">{formatInr(effective)}</span>
                          </>
                        ) : (
                          formatInr(effective)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TableAutoPriceHikePanel;
